import {
  calculateDynamicDateRange,
  calculatePresetDateRange,
  validateDynamicDateRange,
} from "@/utils/date/dateRange"
import {
  DATE_RANGE_PRESET,
  type DateRangePreset,
  type DateRangeUnit,
} from "@/utils/date/dateRangeTypes"

import type { GlobalDateRange } from "../types/core"

export type RelativeDashboardDateRange =
  | { kind: "preset"; preset: DateRangePreset }
  | { kind: "dynamic"; amount: number; unit: DateRangeUnit }

const PRESETS = new Set<DateRangePreset>([
  DATE_RANGE_PRESET.TODAY,
  DATE_RANGE_PRESET.YESTERDAY,
  DATE_RANGE_PRESET.LAST_1_DAY,
  DATE_RANGE_PRESET.LAST_3_DAYS,
  DATE_RANGE_PRESET.LAST_7_DAYS,
  DATE_RANGE_PRESET.LAST_14_DAYS,
  DATE_RANGE_PRESET.LAST_30_DAYS,
])

export function parseRelativeDashboardDateRange(
  value: string | null | undefined,
): RelativeDashboardDateRange | null {
  if (!value) {
    return null
  }
  const [kind, argument, unit, extra] = value.split("/")
  if (
    kind === "preset" &&
    unit === undefined &&
    extra === undefined &&
    PRESETS.has(argument as DateRangePreset)
  ) {
    return { kind, preset: argument as DateRangePreset }
  }
  if (
    kind === "dynamic" &&
    extra === undefined &&
    /^(?:[1-9]\d*)$/.test(argument ?? "") &&
    (unit === "days" || unit === "weeks" || unit === "months") &&
    validateDynamicDateRange(Number(argument), unit).isValid
  ) {
    return { kind, amount: Number(argument), unit }
  }
  return null
}

export function serializeRelativeDashboardDateRange(
  range: RelativeDashboardDateRange,
): string {
  if (range.kind === "preset") {
    return `preset/${range.preset}`
  }
  return `dynamic/${range.amount}/${range.unit}`
}

export function calculateRelativeDashboardDateRange(
  range: RelativeDashboardDateRange,
): GlobalDateRange {
  if (range.kind === "preset") {
    return calculatePresetDateRange(range.preset)
  }
  return calculateDynamicDateRange(range.amount, range.unit)
}
